import React, { useState } from "react";
import BaseRecordRenderer from "@reusables/BaseRecordRenderer";
import { BaseLoadingBlocker } from "@reusables/blockers/BaseLoadingBlocker";
import { useGetSupplierQuery, useUpdateSupplierMutation } from "@redux/features/suppliers/suppliersApi";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import TabsController, { SupplierFormTyping } from "../components/tabs/TabsController";
import { toast } from "react-toastify";
import { isAddressFilled, removeEmpty, toastError } from "@helpers/utils";
import _ from "lodash";
import { Supplier } from "@/types/general";
import { isErrorWithMessage } from "@redux/api/query";
import { Can } from "@/casl.config";
import NoPermissionBanner from "@/components/ErrorPages/NoPermissionBanner";
import RoleDemotionConfirmationModal from "@components/Dashboard/pages/shared/RoleDemotionConfirmationModal";

export default function SupplierEditingPage() {
    const {t} = useTranslation("", {keyPrefix: "suppliers"});

    const history = useHistory();
    const {id} = useParams<{ id: string }>();

    const {data: supplier, isLoading: supplierLoading} = useGetSupplierQuery(parseInt(id));

    const [updateSupplier, {isLoading: isUpdatingLoading}] = useUpdateSupplierMutation();

    const [forceDemotionRequest, setForceDemotionRequest] = useState<{
        data: SupplierFormTyping;
        message: string;
    }>();

    const submitSupplierUpdate = (data: SupplierFormTyping, force = false) => {
        if (!supplier) return;

        const deletedReturnsIds = [...data.deleted_returns];

        const returns: Supplier.DTO.Create["returns"] = data.returns?.reduce((acc: Supplier.DTO.Create["returns"], returns) => {
            const returnsData = _.omit(returns, ["is_primary"]);
            const isFilled = isAddressFilled(returnsData, ["id"]);

            if (isFilled) {
                acc?.push({
                    id: returns.id,
                    name: returns.name ?? "",
                    street: returns.street ?? "",
                    street_2: returns.street_2 ?? "",
                    zipcode: returns.zipcode ?? "",
                    city: returns.city ?? "",
                    country: returns.country?.id ?? undefined,
                    phone: returns.phone ?? "",
                    email: returns.email ?? "",
                    contact_person: returns.contact_person ?? "",
                    is_primary: returns.is_primary ?? false
                });
            } else if (returns.id) {
                deletedReturnsIds.push(returns.id);
            }
            return acc;
        }, []);

        const updateData: Supplier.DTO.Update = {
            name: data.name,
            code: data.code,
            is_customer: data.is_customer,
            ...(force && { force: true }),
            ...(supplier.vat !== data.vat && {vat: data.vat}),
            contacts: data.contacts.filter(con => !_.isEmpty(removeEmpty(con))).map(contact => {
                return {
                    id: contact.id,
                    name: contact.name as string,
                    phone: contact.phone as string,
                    email: contact.email as string
                };
            }),
            ...(isAddressFilled(data.billing_address, ["is_used_for_return"]) && {
                billing: {
                    name: data.billing_address.name,
                    street: data.billing_address.street,
                    street_2: data.billing_address.street_2,
                    zipcode: data.billing_address.zipcode,
                    city: data.billing_address.city,
                    country: data.billing_address.country?.id,
                    phone: data.billing_address.phone,
                    email: data.billing_address.email,
                    is_used_for_return: data.billing_address.is_used_for_return ?? false,
                }
            }),
            returns: returns,
            deleted_contacts: data.deleted_contacts,
            deleted_returns: deletedReturnsIds,

            ...(data.currency && {currency: data.currency.id}),
            ...(data.tax_rate && {tax_rate: data.tax_rate.id}),
            ...(data.payment_terms && {payment_terms: data.payment_terms.id}),
            ...(data.delivery_terms && {delivery_terms: data.delivery_terms.id}),
            ...(data.language && {language: data.language.id}),
        };

        const isCustomerDemotion = !!supplier.is_customer && !data.is_customer;

        updateSupplier({id: supplier.id, ...updateData}).unwrap().then(() => {
            setForceDemotionRequest(undefined);
            history.push(`/dashboard/suppliers`);

            toast.success(t("editing.responses.success"));
        }).catch(e => {
            if (!force && isCustomerDemotion && isErrorWithMessage(e) && e.code === 422) {
                setForceDemotionRequest({
                    data,
                    message: e.message
                });
                return;
            }

            toastError(e);
        });
    };

    return (
        <>
            <Can not I="edit" a="supplier">
                <div className="levitation-extended">
                    <NoPermissionBanner />
                </div>
            </Can>

            <Can I="edit" a="supplier">
                <BaseRecordRenderer
                    record={supplier}
                    loading={supplierLoading}
                >
                    <BaseLoadingBlocker active={isUpdatingLoading}>
                        <div className="space-y-[40px]">
                            <div className='flex items-center' id={"supplier-header"}>
                                <h6 className='text-accent grow'>{supplier?.name}</h6>
                            </div>
                            <TabsController
                                supplier={supplier}
                                onSubmit={submitSupplierUpdate}

                            />
                        </div>
                    </BaseLoadingBlocker>
                </BaseRecordRenderer>
            </Can>
            <RoleDemotionConfirmationModal
                isOpen={!!forceDemotionRequest}
                isLoading={isUpdatingLoading}
                title={t("general.modals.roleDemotion.heading")}
                message={forceDemotionRequest?.message}
                cancelText={t("general.modals.roleDemotion.buttons.cancel")}
                confirmText={t("general.modals.roleDemotion.buttons.confirm")}
                onCancel={() => setForceDemotionRequest(undefined)}
                onConfirm={() => {
                    if (!forceDemotionRequest) return;

                    submitSupplierUpdate(forceDemotionRequest.data, true);
                }}
            />
        </>
    )
}
